import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/layout/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { getVacations, createVacation, updateVacation, cancelVacation, getVacationBalance, getEmployees } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { StatusBadge } from '../components/app';
import { CalendarBlank, Plus, CheckCircle, XCircle, Clock, SunHorizon, ArrowClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function VacationsPage() {
  const { isSpanish } = useLanguage();
  const { user } = useAuth();
  const [vacations, setVacations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [formData, setFormData] = useState({
    employee_id: '', vacation_type: 'vacaciones', start_date: '', end_date: '', days_requested: 0, reason: '', notes: ''
  });

  const isManager = ['admin', 'gerencia', 'rrhh'].includes(user?.role);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, [filterStatus]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      const [vacRes, empRes] = await Promise.all([
        getVacations(params),
        isManager ? getEmployees() : Promise.resolve({ data: [] })
      ]);
      setVacations(vacRes.data);
      setEmployees(empRes.data || []);
    } catch (error) {
      toast.error(isSpanish ? 'Error al cargar datos' : 'Error loading data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createVacation(formData);
      toast.success(isSpanish ? 'Solicitud creada' : 'Request created');
      setIsDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleApprove = async (id) => {
    try {
      await updateVacation(id, { status: 'aprobada' });
      toast.success(isSpanish ? 'Solicitud aprobada' : 'Request approved');
      loadData();
    } catch (error) { toast.error('Error'); }
  };

  const handleReject = async (id) => {
    const reason = window.prompt(isSpanish ? 'Razon del rechazo:' : 'Rejection reason:');
    if (!reason) return;
    try {
      await updateVacation(id, { status: 'rechazada', rejection_reason: reason });
      toast.success(isSpanish ? 'Solicitud rechazada' : 'Request rejected');
      loadData();
    } catch (error) { toast.error('Error'); }
  };

  const handleCancel = async (id) => {
    try {
      await cancelVacation(id);
      toast.success(isSpanish ? 'Solicitud cancelada' : 'Request cancelled');
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Error'); }
  };

  const vacationTypes = [
    { value: 'vacaciones', label: 'Vacaciones' },
    { value: 'licencia_medica', label: 'Licencia Medica' },
    { value: 'licencia_personal', label: 'Licencia Personal' },
    { value: 'permiso', label: 'Permiso' },
    { value: 'maternidad', label: 'Maternidad' },
    { value: 'paternidad', label: 'Paternidad' },
    { value: 'duelo', label: 'Duelo' },
    { value: 'otro', label: 'Otro' },
  ];

  return (
    <Layout>
      <div className="space-y-6" data-testid="vacations-page">
        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          <div>
            <h1 className="font-['Outfit'] text-3xl font-semibold text-[#F3F4F6]">
              <SunHorizon className="w-8 h-8 inline mr-2 text-[#E2B05E]" />
              {isSpanish ? 'Vacaciones y Ausencias' : 'Vacations & Absences'}
            </h1>
            <p className="text-[#9CA3AF] mt-1">{vacations.length} {isSpanish ? 'solicitudes' : 'requests'}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadData} className="border-[#242E42] text-[#9CA3AF]">
              <ArrowClockwise className="w-4 h-4" />
            </Button>
            <Button onClick={() => setIsDialogOpen(true)} className="bg-[#E2B05E] hover:bg-[#F0C47A] text-[#0B0E14]">
              <Plus className="w-5 h-5 mr-2" />
              {isSpanish ? 'Nueva Solicitud' : 'New Request'}
            </Button>
          </div>
        </div>

        <div className="flex gap-3">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-40 valhalla-input"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#151B26] border-[#242E42]">
              <SelectItem value="all">{isSpanish ? 'Todos' : 'All'}</SelectItem>
              <SelectItem value="pendiente">Pendiente</SelectItem>
              <SelectItem value="aprobada">Aprobada</SelectItem>
              <SelectItem value="rechazada">Rechazada</SelectItem>
              <SelectItem value="cancelada">Cancelada</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center min-h-[40vh]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E2B05E]"></div>
          </div>
        ) : vacations.length === 0 ? (
          <div className="valhalla-card p-12 text-center">
            <CalendarBlank className="w-12 h-12 mx-auto text-[#9CA3AF] mb-4" />
            <p className="text-[#F3F4F6] font-semibold">{isSpanish ? 'Sin solicitudes' : 'No requests'}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {vacations.map(v => (
              <div key={v._id} className="valhalla-card p-5">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="text-[#F3F4F6] font-semibold">{v.employee_name || 'N/A'}</p>
                    <p className="text-sm text-[#9CA3AF] capitalize">{v.vacation_type?.replace('_', ' ')}</p>
                  </div>
                  <StatusBadge value={v.status} />
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-[#9CA3AF]">
                    <span>{isSpanish ? 'Desde' : 'From'}:</span>
                    <span className="text-[#F3F4F6]">{v.start_date}</span>
                  </div>
                  <div className="flex justify-between text-[#9CA3AF]">
                    <span>{isSpanish ? 'Hasta' : 'To'}:</span>
                    <span className="text-[#F3F4F6]">{v.end_date}</span>
                  </div>
                  <div className="flex justify-between text-[#9CA3AF]">
                    <span>{isSpanish ? 'Dias' : 'Days'}:</span>
                    <span className="text-[#E2B05E] font-semibold">{v.days_requested}</span>
                  </div>
                  {v.reason && <p className="text-[#9CA3AF] text-xs mt-2 italic">"{v.reason}"</p>}
                </div>
                {v.status === 'pendiente' && (
                  <div className="flex gap-2 mt-4 pt-3 border-t border-[#242E42]">
                    {isManager && (
                      <>
                        <Button size="sm" onClick={() => handleApprove(v._id)} className="flex-1 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 border border-emerald-500/30">
                          <CheckCircle className="w-4 h-4 mr-1" /> {isSpanish ? 'Aprobar' : 'Approve'}
                        </Button>
                        <Button size="sm" onClick={() => handleReject(v._id)} className="flex-1 bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30">
                          <XCircle className="w-4 h-4 mr-1" /> {isSpanish ? 'Rechazar' : 'Reject'}
                        </Button>
                      </>
                    )}
                    <Button size="sm" variant="outline" onClick={() => handleCancel(v._id)} className="border-[#242E42] text-[#9CA3AF]">
                      {isSpanish ? 'Cancelar' : 'Cancel'}
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="bg-[#151B26] border-[#242E42] text-[#F3F4F6] max-w-lg">
            <DialogHeader>
              <DialogTitle>{isSpanish ? 'Nueva Solicitud de Ausencia' : 'New Absence Request'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {isManager && employees.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-[#9CA3AF]">{isSpanish ? 'Empleado' : 'Employee'}</Label>
                  <Select value={formData.employee_id} onValueChange={v => setFormData({...formData, employee_id: v})}>
                    <SelectTrigger className="valhalla-input"><SelectValue placeholder={isSpanish ? 'Seleccionar empleado' : 'Select employee'} /></SelectTrigger>
                    <SelectContent className="bg-[#151B26] border-[#242E42] max-h-60">
                      {employees.map(emp => (
                        <SelectItem key={emp._id} value={emp._id}>{emp.name} {emp.last_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-[#9CA3AF]">{isSpanish ? 'Tipo' : 'Type'}</Label>
                <Select value={formData.vacation_type} onValueChange={v => setFormData({...formData, vacation_type: v})}>
                  <SelectTrigger className="valhalla-input"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#151B26] border-[#242E42]">
                    {vacationTypes.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#9CA3AF]">{isSpanish ? 'Fecha inicio' : 'Start date'}</Label>
                  <Input type="date" value={formData.start_date} onChange={e => setFormData({...formData, start_date: e.target.value})} className="valhalla-input" required />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#9CA3AF]">{isSpanish ? 'Fecha fin' : 'End date'}</Label>
                  <Input type="date" value={formData.end_date} onChange={e => setFormData({...formData, end_date: e.target.value})} className="valhalla-input" required />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-[#9CA3AF]">{isSpanish ? 'Dias solicitados' : 'Days requested'}</Label>
                <Input type="number" min="1" value={formData.days_requested} onChange={e => setFormData({...formData, days_requested: parseInt(e.target.value) || 0})} className="valhalla-input" required />
              </div>
              <div className="space-y-2">
                <Label className="text-[#9CA3AF]">{isSpanish ? 'Razon' : 'Reason'}</Label>
                <Textarea value={formData.reason} onChange={e => setFormData({...formData, reason: e.target.value})} className="valhalla-input" rows={3} />
              </div>
              <div className="flex gap-3 justify-end pt-2">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="border-[#242E42] text-[#9CA3AF]">
                  {isSpanish ? 'Cancelar' : 'Cancel'}
                </Button>
                <Button type="submit" className="bg-[#E2B05E] hover:bg-[#F0C47A] text-[#0B0E14]">
                  {isSpanish ? 'Crear Solicitud' : 'Create Request'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
